import React, { useEffect, useMemo, useRef, useState } from 'react';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const toDatetimeLocalValue = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

const formatAuditDatetimeLabel = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const parseValue = (value) => {
  if (!value) {
    const now = new Date();
    return { date: new Date(now.getFullYear(), now.getMonth(), now.getDate()), hour: now.getHours(), minute: now.getMinutes() };
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const now = new Date();
    return { date: new Date(now.getFullYear(), now.getMonth(), now.getDate()), hour: 0, minute: 0 };
  }
  return {
    date: new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()),
    hour: parsed.getHours(),
    minute: parsed.getMinutes(),
  };
};

const getMonthGrid = (viewDate) => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const isSameDay = (a, b) => (
  a && b
  && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate()
);

const fieldStyle = {
  background: 'linear-gradient(180deg, rgba(10,20,35,0.98), rgba(4,10,19,0.98))',
  color: '#f4e7c0',
  border: '1px solid rgba(201,168,76,0.48)',
  borderRadius: '12px',
  padding: '10px 12px',
  minHeight: '42px',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.09), inset 0 -8px 18px rgba(0,0,0,0.32), 0 0 0 1px rgba(0,0,0,0.28), 0 8px 20px rgba(0,0,0,0.24)',
};

const arrowStyle = {
  ...fieldStyle,
  width: '42px',
  minWidth: '42px',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'pointer',
};

export default function AuditDatetimePicker({
  value,
  onChange,
  placeholder = 'Select date & time',
  open,
  onOpenChange,
  fullWidth = false,
}) {
  const wrapRef = useRef(null);
  const parsed = parseValue(value);
  const [viewMonth, setViewMonth] = useState(() => new Date(parsed.date.getFullYear(), parsed.date.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(parsed.date);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);

  useEffect(() => {
    if (!open) return;
    const next = parseValue(value);
    setSelectedDate(next.date);
    setHour(next.hour);
    setMinute(next.minute);
    setViewMonth(new Date(next.date.getFullYear(), next.date.getMonth(), 1));
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      onOpenChange(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, onOpenChange]);

  const monthCells = useMemo(() => getMonthGrid(viewMonth), [viewMonth]);
  const monthLabel = viewMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const toggle = () => onOpenChange(!open);

  const commit = () => {
    const next = new Date(selectedDate);
    next.setHours(hour, minute, 0, 0);
    onChange(toDatetimeLocalValue(next));
    onOpenChange(false);
  };

  const clearValue = () => {
    onChange('');
    onOpenChange(false);
  };

  const shiftMonth = (delta) => {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        minWidth: fullWidth ? 0 : '220px',
        width: fullWidth ? '100%' : undefined,
        flex: fullWidth ? '1 1 100%' : undefined,
        zIndex: open ? 120 : 1,
      }}
    >
      <div style={{ display: 'flex', gap: '6px', alignItems: 'stretch' }}>
        <div
          style={{ ...fieldStyle, flex: 1, minWidth: 0, textAlign: 'left', cursor: 'default', display: 'flex', alignItems: 'center' }}
          aria-live="polite"
        >
          {value ? formatAuditDatetimeLabel(value) : placeholder}
        </div>
        <button
          type="button"
          onClick={toggle}
          style={arrowStyle}
          aria-expanded={open}
          aria-label={open ? 'Close calendar' : 'Open calendar'}
        >
          <span
            style={{
              color: '#f5d88a',
              fontSize: '11px',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 220ms ease',
            }}
            aria-hidden
          >
            {'\u25BC'}
          </span>
        </button>
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            minWidth: fullWidth ? 'min(300px, calc(100vw - 24px))' : '300px',
            maxWidth: fullWidth ? 'calc(100vw - 24px)' : undefined,
            zIndex: 200,
            borderRadius: '14px',
            border: '1px solid rgba(245,216,138,0.45)',
            background: 'radial-gradient(circle at 10% -30%, rgba(245,216,138,0.22), transparent 42%), linear-gradient(165deg, rgba(14,26,44,0.98), rgba(6,12,22,0.99))',
            boxShadow: '0 22px 48px rgba(0,0,0,0.5), 0 0 28px rgba(201,168,76,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
            padding: '12px',
            animation: 'auditSoftOpen 260ms ease',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', color: '#f7df9b', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer' }}
            >
              {'\u2039'}
            </button>
            <span style={{ color: '#f7df9b', fontSize: '13px', fontWeight: 700, letterSpacing: '0.3px' }}>{monthLabel}</span>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', color: '#f7df9b', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer' }}
            >
              {'\u203A'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
            {WEEKDAYS.map((d) => (
              <span key={d} style={{ textAlign: 'center', color: '#8896a8', fontSize: '10px', fontWeight: 700 }}>{d}</span>
            ))}
            {monthCells.map((cell, idx) => {
              if (!cell) return <span key={`empty-${idx}`} />;
              const selected = isSameDay(cell, selectedDate);
              const today = isSameDay(cell, new Date());
              return (
                <button
                  key={cell.toISOString()}
                  type="button"
                  onClick={() => setSelectedDate(cell)}
                  style={{
                    border: selected ? '1px solid rgba(245,216,138,0.9)' : today ? '1px solid rgba(201,168,76,0.45)' : '1px solid transparent',
                    background: selected
                      ? 'linear-gradient(145deg, rgba(201,168,76,0.35), rgba(201,168,76,0.12))'
                      : 'rgba(255,255,255,0.03)',
                    color: selected ? '#fff3c4' : '#d2d8e2',
                    borderRadius: '8px',
                    height: '32px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: selected ? 700 : 500,
                    boxShadow: selected ? '0 0 14px rgba(201,168,76,0.35)' : 'none',
                  }}
                >
                  {cell.getDate()}
                </button>
              );
            })}
          </div>

          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              marginBottom: '10px',
              padding: '8px',
              borderRadius: '10px',
              border: '1px solid rgba(201,168,76,0.22)',
              background: 'rgba(0,0,0,0.25)',
            }}
          >
            <span style={{ color: '#8896a8', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>Time</span>
            <select
              value={hour}
              onChange={(e) => setHour(Number(e.target.value))}
              style={{ ...fieldStyle, flex: 1, minHeight: '36px', padding: '6px 8px', fontSize: '12px' }}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
              ))}
            </select>
            <span style={{ color: '#f7df9b' }}>:</span>
            <select
              value={minute}
              onChange={(e) => setMinute(Number(e.target.value))}
              style={{ ...fieldStyle, flex: 1, minHeight: '36px', padding: '6px 8px', fontSize: '12px' }}
            >
              {Array.from({ length: 60 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={clearValue}
              style={{ flex: 1, background: 'transparent', border: '1px solid rgba(148,163,184,0.35)', color: '#a7b4c6', borderRadius: '10px', padding: '8px', cursor: 'pointer', fontSize: '12px' }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={commit}
              style={{
                flex: 1,
                background: 'linear-gradient(112deg, rgba(255,228,156,0.24), rgba(194,146,54,0.35), rgba(11,20,34,0.86))',
                border: '1px solid rgba(245,216,138,0.78)',
                color: '#ffebad',
                borderRadius: '10px',
                padding: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 700,
                boxShadow: '0 8px 18px rgba(0,0,0,0.32), 0 0 14px rgba(201,168,76,0.22)',
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
